import type { AxiosInstance } from 'axios';
import type { AuthService } from 'types/AuthService';
import { INVOICE_DATA_ENDPOINTS } from './endpoints';
import type {
  ApiEnvelope,
  InvoiceDataApiResponse,
  CreateInvoiceDataApiRequest,
  UpdateInvoiceDataApiRequest,
  FiscalRegimesApiResponse,
  InvoiceActionApiQuery,
  InvoiceActionApiResponse,
} from './types';

/** HTTP client type for invoice data operations */
export type InvoiceDataHttpClientType = {
  create: (request: CreateInvoiceDataApiRequest) => Promise<InvoiceDataApiResponse>;
  update: (
    invoiceDataUuid: string,
    request: UpdateInvoiceDataApiRequest,
    includeDeleted?: boolean,
  ) => Promise<InvoiceDataApiResponse>;
  retry: (paymentUuid: string, query: InvoiceActionApiQuery) => Promise<InvoiceActionApiResponse>;
  list: () => Promise<InvoiceDataApiResponse[]>;
  getById: (invoiceDataUuid: string, includeDeleted?: boolean) => Promise<InvoiceDataApiResponse>;
  delete: (invoiceDataUuid: string) => Promise<void>;
  listFiscalRegimes: () => Promise<FiscalRegimesApiResponse>;
};

/**
 * Creates the HTTP client for invoice data API operations.
 *
 * CRUD endpoints target parrot-subscriptions-api; `listFiscalRegimes`
 * stays on the legacy backend since the new API does not expose it yet.
 *
 * @param props.httpClientSubscription - Axios bound to parrot-subscriptions-api
 * @param props.httpClientApi - Axios bound to the legacy backend
 * @param props.authService - Authentication service (used by legacy regime lookup)
 */
export const createInvoiceDataHttpClient = (props: {
  httpClientSubscription: AxiosInstance;
  httpClientApi: AxiosInstance;
  authService: AuthService;
}): InvoiceDataHttpClientType => {
  const { httpClientSubscription, httpClientApi, authService } = props;

  const create = async (request: CreateInvoiceDataApiRequest): Promise<InvoiceDataApiResponse> => {
    const response = await httpClientSubscription.post<ApiEnvelope<InvoiceDataApiResponse>>(
      INVOICE_DATA_ENDPOINTS.BASE,
      request,
    );
    return response.data.data;
  };

  const update = async (
    invoiceDataUuid: string,
    request: UpdateInvoiceDataApiRequest,
    includeDeleted = false,
  ): Promise<InvoiceDataApiResponse> => {
    const response = await httpClientSubscription.patch<ApiEnvelope<InvoiceDataApiResponse>>(
      INVOICE_DATA_ENDPOINTS.BY_ID(invoiceDataUuid),
      request,
      includeDeleted ? { params: { include_deleted: true } } : undefined,
    );
    return response.data.data;
  };

  // reinvoice-sync takes no body: it decides on its own to cancel, drop and re-issue, and the
  // only input is the scope, which rides in the query string.
  const retry = async (paymentUuid: string, query: InvoiceActionApiQuery): Promise<InvoiceActionApiResponse> => {
    const response = await httpClientSubscription.post<ApiEnvelope<InvoiceActionApiResponse>>(
      INVOICE_DATA_ENDPOINTS.INVOICE_ACTION(paymentUuid),
      undefined,
      { params: query },
    );
    return response.data.data;
  };

  const list = async (): Promise<InvoiceDataApiResponse[]> => {
    const response = await httpClientSubscription.get<ApiEnvelope<InvoiceDataApiResponse[]>>(
      INVOICE_DATA_ENDPOINTS.BASE,
    );
    return response.data.data;
  };

  /**
   * Reads one fiscal record. `includeDeleted` asks the API for a soft-deleted one, which the
   * listing hides: a retry link points at the record its payment was invoiced with, and that
   * record may have been deleted since.
   */
  const getById = async (invoiceDataUuid: string, includeDeleted = false): Promise<InvoiceDataApiResponse> => {
    const response = await httpClientSubscription.get<ApiEnvelope<InvoiceDataApiResponse>>(
      INVOICE_DATA_ENDPOINTS.BY_ID(invoiceDataUuid),
      includeDeleted ? { params: { include_deleted: true } } : undefined,
    );
    return response.data.data;
  };

  const deleteFn = async (invoiceDataUuid: string): Promise<void> => {
    await httpClientSubscription.delete(INVOICE_DATA_ENDPOINTS.BY_ID(invoiceDataUuid));
  };

  const listFiscalRegimes = async (): Promise<FiscalRegimesApiResponse> => {
    const storeUuid = authService?.store?.uuid;

    if (!storeUuid) {
      throw new Error('Store UUID is required to list fiscal regimes');
    }

    const response = await httpClientApi.get<FiscalRegimesApiResponse>(
      `${INVOICE_DATA_ENDPOINTS.FISCAL_REGIMES}?store_uuid=${storeUuid}`,
    );
    return response.data;
  };

  return { create, update, retry, list, getById, delete: deleteFn, listFiscalRegimes };
};
